import type { GaVueComponent } from "@/common/vueUtils";
import { type AuthorListView, type ContentId, type ContentMetadata, Language } from "@newgenerated/shared/schema";
import { GaBadge } from "@/components/general/GaBadge";
import { useGaContext } from "@utils/vue-migration/common/gaContext/gaContext";
import "./_content-card.scss";
import type { DeepReadonly } from "vue";
import { type BookmarkStatusHandler, type ContentProgressState, getContentProgressState } from "@/common/ixStatusUtils";
import { ActionButton } from "@/components/cards/ActionButton";
import { enrichRelativeUrl } from "@utils/vue-migration/common/urlUtils";
import type { AspContext, CustomerContext, GetTranslationFunctionFn } from "@utils/widgetContextUtils";
import { showQuickWinPageAsModal } from "@/components/quickwin/QuickWinPage";
import { ContentTypeBadge } from "@/components/badges/ContentTypeBadge";
import { formatAuthorName } from "@/common/authorUtils";
import type { TranslationFn } from "@utils/vue-migration/translationUtils";

type Layout = "vertical" | "horizontal";

function ProgressIndicator(props: { progressState: ContentProgressState }): GaVueComponent {
  if (props.progressState === "NONE") return <></>;
  return (
    <div class={`progress-indicator progress-indicator--${props.progressState.toLowerCase()}`}>
      <i class={props.progressState === "STARTED" ? "ico-half-progress-circle-fill" : "ico-check-circle-fill"} />
    </div>
  );
}

function getLink(path: string | null, additionalParams: URLSearchParams | null): string | null {
  if (path === null || path === "none") {
    return null;
  }

  try {
    const link = new URL(path, location.href);
    if (additionalParams !== null) {
      for (const [key, value] of additionalParams) {
        link.searchParams.append(key, value);
      }
    }
    return link.pathname + link.search;
  } catch (_) {
    return null;
  }
}

function getAuthorDetails(content: DeepReadonly<ContentMetadata>): DeepReadonly<AuthorListView[]> | undefined {
  if ("authors" in content) {
    return content.authors;
  } else if ("authorsDetail" in content) {
    return content.authorsDetail.map((author) => ({
      authorId: 0,
      firstName: author.name,
      gender: author.gender,
      lastName: "",
      middleName: "",
    }));
  }
  return undefined;
}

function Cover(props: { content: DeepReadonly<ContentMetadata> }): GaVueComponent {
  switch (props.content.contentType) {
    case "SUMMARY": {
      return (
        <div class="card-cover-background--summary" style={props.content.backgroundColor !== null ? "--card-cover-bg-color: " + props.content.backgroundColor + ";" : ""}>
          <div class="card-cover-round-circle">
            <object class="card-cover-img" data={props.content.common.coverUri + "?s=M"} type="image/jpeg">
              {
                // Fallback image: gray (400) surface
              }
              <img class="card-cover-img-fallback" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR4AWNYuenIfwAHRgMfaGsqcAAAAABJRU5ErkJggg==" alt="" />
            </object>
          </div>
        </div>
      );
    }
    case "MIAB": {
      return (
        <div class="card-cover-background--miab">
          <img src="/www/images/miab/layer-1.svg" class="card-cover-background--miab-layer" alt="" />
          <object class="card-cover-img" data={props.content.common.coverUri + "?s=S"} type="image/jpeg">
            {
              // Fallback image: gray (400) surface
            }
            <img class="card-cover-img-fallback" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR4AWNYuenIfwAHRgMfaGsqcAAAAABJRU5ErkJggg==" alt="" />
          </object>
          <img src="/www/images/miab/layer-2.svg" class="card-cover-background--miab-layer2" alt="" />
        </div>
      );
    }
    case "ACTIONABLE": {
      return (
        <object class="card-cover-img" data={props.content.common.coverUri}>
          {
            // < !--Fallback image: gray(200) surface-- >
          }
          <img class="card-cover-img-fallback" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR4AWO4+/j1fwAI9wOrhrsKQwAAAABJRU5ErkJggg==" alt="" />
        </object>
      );
    }
    case "ONLINE_CONTENT": {
      const backgroundColor = "#9AF1A9";
      const onlineContentClasses = ["card-cover-background--online-content", `card-cover-background--online-content-${props.content.formatType.toLowerCase()}`];
      return (
        <div class={onlineContentClasses} style={"--card-cover-bg-color: " + backgroundColor + ";"}>
          <div class="card-cover-round-circle">
            <img class="card-cover-img" src={props.content.common.coverUri} alt="" />
          </div>
        </div>
      );
    }
    case "EXPERT_VIDEO": {
      return (
        <div class="card-cover-background--webinar">
          <object class="card-cover-img" data={props.content.common.coverUri} type="image/jpeg">
            {
              // Fallback image: gray (400) surface
            }
            <img class="card-cover-img-fallback" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR4AWNYuenIfwAHRgMfaGsqcAAAAABJRU5ErkJggg==" alt="" />
          </object>
        </div>
      );
    }
    case "QUICKWIN": {
      // Only reachable via `originalCard` - the default quickwin card has no cover at all (see
      // ContentCard's own QUICKWIN branch). `coverUri` here is the underlying summary's own cover
      // (see `QuickWinOutline.toMetadata`), so the same round-circle-on-tint treatment ONLINE_CONTENT uses
      // for its own cover fits directly - just under its own warning/amber tint to match the
      // "Skill Boost" badge's own color rather than reusing ONLINE_CONTENT's blue.
      return (
        <div class="card-cover-background--quickwin">
          <div class="card-cover-round-circle">
            <img class="card-cover-img" src={props.content.common.coverUri} alt="" />
          </div>
        </div>
      );
    }
  }
}

function Badges(props: { translationFn: TranslationFn; content: DeepReadonly<ContentMetadata>; length: number | null; progressState: ContentProgressState; originalCard?: boolean }): GaVueComponent {
  function Loaded(p: { t: TranslationFn }): GaVueComponent | null {
    const progressState = props.progressState;

    function AudioBadge(): GaVueComponent | null {
      if (props.content.contentType === "SUMMARY" && props.content.common.audioActive) {
        return <GaBadge variant={props.content.documentType === "REVIEW" ? "neutral-warm-light" : "primary-light"} icon="ico-earphones" />;
      }
      return null;
    }

    function MiabBadge(): GaVueComponent | null {
      if (props.content.contentType === "SUMMARY" && props.content.miabId !== null) {
        return <GaBadge variant={props.content.documentType === "REVIEW" ? "neutral-warm-light" : "primary-light"} icon="ico-people" />;
      }
      return null;
    }

    function LengthBadge(): GaVueComponent | null {
      if (props.content.contentType === "QUICKWIN" && progressState !== "NONE") {
        return null;
      }

      if (props.length !== null && props.length > 0) {
        return (
          <div class="card-badges-time caption">
            <i class="ico-clock" aria-hidden="true" />
            <span>{props.length + " Min"}</span>
          </div>
        );
      }

      return null;
    }

    function ProgressBadge(): GaVueComponent | null {
      // `originalCard` shows progress in the corner instead (`Actions`, same spot every other
      // type's bookmark sits in) - showing it again here would be a second, redundant signal.
      if (props.originalCard === true) {
        return null;
      }
      if (props.content.contentType === "QUICKWIN" && progressState !== "NONE") {
        return (
          <div class="card-badges-progress">
            <div class={`progress-indicator--${progressState.toLowerCase()}`}>
              <i class={progressState === "STARTED" ? "ico-half-progress-circle-fill" : "ico-check-circle-fill"} />
            </div>
          </div>
        );
      }
      return null;
    }
    return (
      <>
        <ContentTypeBadge content={props.content} t={p.t} />
        <AudioBadge />
        <MiabBadge />
        <LengthBadge />
        <ProgressBadge />
      </>
    );
  }

  return (
    <div class={`${props.length !== null && props.length > 0 ? "card-badges--with-length" : "card-badges "}`}>
      <Loaded t={props.translationFn} />
    </div>
  );
}

function Body(props: {
  content: DeepReadonly<ContentMetadata>;
  link: string | null;
  target: string | null;
  contentCtx: { language: Language; t: TranslationFn };
  ctx: { asp: AspContext };
  length: number | null;
  progressState: ContentProgressState;
  originalCard?: boolean;
}): GaVueComponent {
  const { t } = props.contentCtx;
  const authors = getAuthorDetails(props.content);

  const shortedFormat = authors?.map((a) => formatAuthorName(a, true));
  const formatted = shortedFormat !== undefined ? new Intl.ListFormat(props.contentCtx.language).format(shortedFormat) : null;

  return (
    <div class="card-body">
      <Badges translationFn={t} content={props.content} length={props.length} progressState={props.progressState} originalCard={props.originalCard} />
      {formatted !== null ? (
        props.link !== null ? (
          <a
            href={enrichRelativeUrl(props.link, {
              currentLanguage: props.contentCtx.language,
              asp: props.ctx.asp,
            })}
            target={props.target ?? undefined}
            tabindex="-1">
            <p class="card-author small" title={formatted}>
              {formatted}
            </p>
          </a>
        ) : (
          <p class="card-author small" title={formatted}>
            {formatted}
          </p>
        )
      ) : (props.content.contentType !== "QUICKWIN" || props.originalCard === true) && props.content.common.leadIn !== null && props.content.common.leadIn.trim() !== "" ? (
        props.link !== null ? (
          <a
            href={enrichRelativeUrl(props.link, {
              currentLanguage: props.contentCtx.language,
              asp: props.ctx.asp,
            })}
            target={props.target ?? undefined}
            tabindex="-1">
            <p class="card-author small" title={props.content.common.leadIn}>
              {props.content.common.leadIn}
            </p>
          </a>
        ) : (
          <p class="card-author small" title={props.content.common.leadIn}>
            {props.content.common.leadIn}
          </p>
        )
      ) : null}

      {props.content.common.title.trim() !== "" ? (
        props.link !== null ? (
          <a
            class="focus-ring d-block"
            href={enrichRelativeUrl(props.link, {
              currentLanguage: props.contentCtx.language,
              asp: props.ctx.asp,
            })}
            target={props.target ?? undefined}>
            <h4 class="card-title" title={props.content.common.title}>
              {props.content.common.title}
            </h4>
          </a>
        ) : (
          <h4 class="card-title" title={props.content.common.title}>
            {props.content.common.title}
          </h4>
        )
      ) : null}

      {props.content.common.description !== null && props.content.common.description.trim() !== "" ? (
        props.link !== null ? (
          <a
            href={enrichRelativeUrl(props.link, {
              currentLanguage: props.contentCtx.language,
              asp: props.ctx.asp,
            })}
            target={props.target ?? undefined}
            tabindex="-1">
            <p class="card-text" title={props.content.common.description}>
              {props.content.common.description}
            </p>
          </a>
        ) : (
          <p class="card-text" title={props.content.common.description}>
            {props.content.common.description}
          </p>
        )
      ) : null}
    </div>
  );
}

function BookmarkAction(props: { bookmarkHandler: BookmarkStatusHandler | null; contentId: ContentId }): GaVueComponent | null {
  const ixStatus = props.bookmarkHandler?.getIxStatus(props.contentId);
  if (ixStatus === undefined) {
    return null;
  }
  return (
    <ActionButton onClick={() => props.bookmarkHandler?.updateBookmark(props.contentId)} disabled={ixStatus.bookmarkedAt.status === "DISABLED"}>
      {ixStatus.bookmarkedAt.value !== null ? <i class="ico-bookmark-fill"></i> : <i class="ico-bookmark"></i>}
    </ActionButton>
  );
}

function Actions(props: { content: DeepReadonly<ContentMetadata>; bookmarkHandler: BookmarkStatusHandler | null; progressState: ContentProgressState }): GaVueComponent | null {
  const { flags } = useGaContext();

  // Skill Boosts aren't bookmarkable (see `showBookmark` below) - this is the `originalCard` slot
  // where every other type's bookmark button sits, showing completion state there instead once
  // there is any, rather than leaving that corner empty.
  if (props.content.contentType === "QUICKWIN") {
    if (props.progressState === "NONE") {
      return null;
    }
    return (
      <div class="card-actions">
        <div class="card-actions-right">
          <div class={["quickwin-progress-action", `quickwin-progress-action--${props.progressState.toLowerCase()}`]}>
            <i class={props.progressState === "STARTED" ? "ico-half-progress-circle-fill" : "ico-check-circle-fill"} />
          </div>
        </div>
      </div>
    );
  }

  const showBookmark = (props.content.contentType === "SUMMARY" || props.content.contentType === "MIAB" || props.content.contentType === "ACTIONABLE" || props.content.contentType === "ONLINE_CONTENT") && flags().loggedIn;

  if (!showBookmark) {
    return null;
  }

  return (
    <div class="card-actions">
      <div class="card-actions-right">
        <BookmarkAction bookmarkHandler={props.bookmarkHandler} contentId={props.content.contentId} />
      </div>
    </div>
  );
}

export type ContentCardContext = {
  getTranslationFunction: GetTranslationFunctionFn;
  customer: CustomerContext;
  asp: AspContext;
};

export function ContentCard(props: {
  content: DeepReadonly<ContentMetadata>;
  layout: Layout;
  link?: string;
  additionalParams?: URLSearchParams;
  target?: string;
  bookmarkHandler?: BookmarkStatusHandler;
  length?: number;
  ctx: ContentCardContext;
  onCompleted?: () => void;
  /**
   * QUICKWIN only - false (default) keeps today's dedicated quickwin layout (see below) unchanged
   * everywhere it's already used. True instead renders it through the same Cover/Body/Actions
   * shape every other content type uses, for a caller that mixes Skill Boosts into a list of
   * other content types and needs one consistent look across the lot (`MixedListCustomPortlet`).
   * Ignored for every other content type.
   */
  originalCard?: boolean;
}): GaVueComponent {
  const language = props.content.common.language;
  const t = props.ctx.getTranslationFunction(language);

  const ixStatus = props.bookmarkHandler?.getIxStatus(props.content.contentId);
  const progressState = getContentProgressState(ixStatus);

  // special handling for quickwin as design is not really following the card pattern
  if (props.content.contentType === "QUICKWIN") {
    const selectedAnswer = ixStatus?.contentType === "QUICKWIN" ? ixStatus.selectedAnswerIndex.value : null;
    const onIxStatusChanged = (): void => {
      props.bookmarkHandler?.refreshIxStatus(props.content.contentId);
    };
    const onClickOpenModal = (): void => {
      void showQuickWinPageAsModal(Number(props.content.contentId.id), props.ctx, onIxStatusChanged, props.onCompleted, selectedAnswer);
    };

    if (props.originalCard === true) {
      const cardClasses = ["content-card", "content-card--" + props.layout, props.content.common.inactive ? "content-card--inactive" : "", props.content.common.language === "ar" ? "default-arabic-typography" : "default-typography"];
      return (
        <div class={cardClasses} lang={props.content.common.language} dir={Language.getDir(props.content.common.language)}>
          <div class="card-cover">
            <a onClick={onClickOpenModal} tabindex="-1">
              <Cover content={props.content} />
            </a>
          </div>
          <a onClick={onClickOpenModal} class="focus-ring d-block card-body-link">
            {
              // No time estimate here, same as every other type in a mixed slider: none of them
              // get a `length` from `MixedListCustomPortlet.tsx` either (it never passes one), so
              // this just brings Skill Boost's badge row in line with its slider siblings instead
              // of being the only item showing one.
            }
            <Body content={props.content} link={null} target={null} ctx={props.ctx} contentCtx={{ language, t }} length={null} progressState={progressState} originalCard={true} />
          </a>
          <Actions content={props.content} bookmarkHandler={props.bookmarkHandler ?? null} progressState={progressState} />
        </div>
      );
    }

    const length = props.content.quickWinType === "INSIGHT" ? 3 : 4;
    const cardClasses = ["content-card", props.content.common.language === "ar" ? "default-arabic-typography" : "default-typography"];
    return (
      <div class={cardClasses}>
        <a onClick={onClickOpenModal} class="card-actions-quickwin d-flex flex-column justify-content-between">
          <Body content={props.content} link={null} target={null} ctx={props.ctx} contentCtx={{ language, t }} length={length} progressState={progressState} />
          <hr />
          <p class="card-based-on p-2" title={`${t("quickwin:basedOn")} ${props.content.common.leadIn}`}>
            <div class="card-based-on__content">
              <object class="card-based-on__cover" data={props.content.common.coverUri + "?s=S"} type="image/jpeg">
                {
                  // Fallback image: gray (400) surface
                }
                <img class="card-based-on__cover" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR4AWNYuenIfwAHRgMfaGsqcAAAAABJRU5ErkJggg==" alt="" />
              </object>
              <div class="card-based-on__text">
                <div class="caption">{t("quickwin:basedOn")}</div>
                <div class="card-based-on__book-title">{props.content.common.leadIn}</div>
              </div>
            </div>
          </p>
        </a>
      </div>
    );
  } else {
    const cardLink = getLink(props.link ?? props.content.common.link, props.additionalParams ?? null);
    const cardClasses = [
      "content-card",
      "content-card--" + props.layout,
      props.content.common.inactive ? "content-card--inactive" : "",
      cardLink === null || cardLink.trim() === "" ? "content-card--no-link" : "",
      props.content.common.language === "ar" ? "default-arabic-typography" : "default-typography",
    ];
    return (
      <div class={cardClasses} lang={props.content.common.language} dir={Language.getDir(props.content.common.language)}>
        <div class="card-cover">
          <ProgressIndicator progressState={progressState} />
          {cardLink !== null ? (
            <a
              href={enrichRelativeUrl(cardLink, {
                currentLanguage: language,
                asp: props.ctx.asp,
              })}
              target={props.target}
              tabindex="-1">
              <Cover content={props.content} />
            </a>
          ) : (
            <Cover content={props.content} />
          )}
        </div>
        <Body content={props.content} link={cardLink} target={props.target ?? null} ctx={props.ctx} contentCtx={{ language, t }} length={props.length ?? null} progressState={progressState} />
        <Actions content={props.content} bookmarkHandler={props.bookmarkHandler ?? null} progressState={progressState} />
      </div>
    );
  }
}
